import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Box, Typography, IconButton, Dialog } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { getDefectPhotos } from '../redux/slice';
import './StaticInfoSection.scss';

const StaticInfoSection = ({ staticInfo, inspectionPhotos = [] }) => {
	const { messages } = useIntl();
	const dispatch = useDispatch();
	const [expanded, setExpanded] = useState(true);
	const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
	const [photoUrls, setPhotoUrls] = useState({});
	const [loading, setLoading] = useState(false);

	if (!staticInfo) return null;

	const toggleExpand = () => {
		setExpanded(!expanded);
	};

	const handlePhotoClick = (index) => {
		setSelectedPhotoIndex(index);
	};

	const handleCloseDialog = () => {
		setSelectedPhotoIndex(null);
	};

	const handlePrevPhoto = () => {
		setSelectedPhotoIndex((prev) => (prev > 0 ? prev - 1 : inspectionPhotos.length - 1));
	};

	const handleNextPhoto = () => {
		setSelectedPhotoIndex((prev) => (prev < inspectionPhotos.length - 1 ? prev + 1 : 0));
	};

	const resolvedPhotos = useMemo(() => {
		if (inspectionPhotos.length === 0) return [];

		return inspectionPhotos.map((photo) => ({
			...photo,
			resolvedUrl: (photo.fileKey && photoUrls[photo.fileKey]) || photo.url || photo.thumbnail,
		}));
	}, [inspectionPhotos, photoUrls]);

	const missingFileKeys = useMemo(() => {
		if (inspectionPhotos.length === 0) return [];

		const seen = new Set();
		return inspectionPhotos
			.map((photo) => photo.fileKey)
			.filter((key) => {
				if (!key || photoUrls[key] || seen.has(key)) return false;
				seen.add(key);
				return true;
			});
	}, [inspectionPhotos, photoUrls]);

	// Fetch full-size photos when dialog opens
	useEffect(() => {
		if (selectedPhotoIndex === null) {
			setLoading(false);
			return undefined;
		}

		if (missingFileKeys.length === 0) {
			setLoading(false);
			return undefined;
		}

		let isCancelled = false;
		setLoading(true);

		dispatch(getDefectPhotos({ fileKeys: missingFileKeys }))
			.unwrap()
			.then((urls) => {
				if (!isCancelled && urls) {
					setPhotoUrls((prev) => ({
						...prev,
						...urls,
					}));
				}
			})
			.catch((error) => {
				if (!isCancelled) {
					console.error('[StaticInfoSection] Failed to fetch inspection photos', error);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [selectedPhotoIndex, missingFileKeys, dispatch]);

	const staticInfoEntries = Object.entries(staticInfo);

	const renderValue = (key, value) => {
		// Check if this is the Camera/Photos row
		const normalizedKey = key.toLowerCase();
		if ((normalizedKey === 'camera / photos' || normalizedKey === 'camera/photos') && inspectionPhotos.length > 0) {
			return (
				<Box className="photos-inline-container">
					<Typography className="photo-count">{value}</Typography>
					<Box className="photos-thumbnails">
						{inspectionPhotos.map((photo, index) => (
							<Box
								key={`photo-${photo.mediaId || index}`}
								className="photo-thumbnail"
								onClick={() => handlePhotoClick(index)}
							>
								<img
									src={photo.url || photo.thumbnail}
									alt={`Photo ${index + 1}`}
									loading="lazy"
								/>
							</Box>
						))}
					</Box>
				</Box>
			);
		}
		return value || '-';
	};

	return (
		<Box className="static-info-section">
			<Box className="section-header">
				<Typography variant="h2" className="section-title">
					{messages.inspectionDetails || 'INSPECTION DETAILS'}
				</Typography>
				<Box className="expand-toggle" onClick={toggleExpand} style={{ cursor: 'pointer' }}>
					<Typography component="span" className="toggle-text">
						{expanded ? (messages.less || 'Less') : (messages.more || 'More')}
					</Typography>
					<IconButton
						size="small"
						className={`expand-icon ${expanded ? 'expanded' : ''}`}
						aria-label={expanded ? 'collapse' : 'expand'}
					>
						<ExpandMoreIcon />
					</IconButton>
				</Box>
			</Box>

			{expanded && (
				<Box className="info-table-container">
					<table className="info-table">
						<tbody>
							{staticInfoEntries.map(([key, value], index) => (
								<tr key={index} className="info-row">
									<td className="info-label">
										{key.toUpperCase()}
									</td>
									<td className="info-value">
										{renderValue(key, value)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Box>
			)}

			{/* Photo Dialog */}
			{selectedPhotoIndex !== null && inspectionPhotos.length > 0 && (
				<Dialog
					open={true}
					onClose={handleCloseDialog}
					maxWidth={false}
					className="photo-dialog"
					PaperProps={{
						style: {
							backgroundColor: 'rgba(0, 0, 0, 0.95)',
							boxShadow: 'none',
							width: '95vw',
							height: '95vh',
						},
					}}
				>
					<Box className="dialog-content">
						<IconButton className="close-button" onClick={handleCloseDialog}>
							<CloseIcon />
						</IconButton>
						<IconButton 
							className="nav-button prev-button" 
							onClick={handlePrevPhoto}
							disabled={inspectionPhotos.length <= 1}
						>
							<NavigateBeforeIcon />
						</IconButton>
						<img
							src={resolvedPhotos[selectedPhotoIndex]?.resolvedUrl || inspectionPhotos[selectedPhotoIndex]?.url || inspectionPhotos[selectedPhotoIndex]?.thumbnail}
							alt={`Photo ${selectedPhotoIndex + 1}`}
							className="dialog-image"
						/>
						<IconButton 
							className="nav-button next-button" 
							onClick={handleNextPhoto}
							disabled={inspectionPhotos.length <= 1}
						>
							<NavigateNextIcon />
						</IconButton>
						<Typography className="photo-info" style={{ 
							position: 'absolute', 
							bottom: 8, 
							left: '50%', 
							transform: 'translateX(-50%)', 
							color: '#fff',
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							padding: '4px 12px',
							borderRadius: '4px'
						}}>
							{selectedPhotoIndex + 1} / {inspectionPhotos.length}
						</Typography>
					</Box>
				</Dialog>
			)}
		</Box>
	);
};

export default StaticInfoSection;
